// utils/prepaidExpenseIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /prepaids/{prepaid_id}/expense ("Apply"/recognition),
// used by pages/prepaids/PrepaidDetail.tsx -- the only frontend caller of
// this endpoint. Modeled directly on utils/depositWriteoffIdempotency.ts,
// since Prepaid recognition is the direct structural analog of Deposit
// write-off (an expense-account-driven consumption of an existing
// balance), not Deposit Apply (which settles a Bill/Invoice).
//
// Per-prepaid slot, NOT a single global slot: PrepaidDetail.tsx is mounted
// once for its route and reused across different prepaid_id route-param
// values without remounting, so the recognition-attempt specification
// requires one independent attempt slot PER prepaid_id -- an unresolved
// ambiguous attempt on one Prepaid must never block, leak into, or be
// confused with a submission on a different Prepaid. This module itself
// only models the state machine for a SINGLE slot (idle/in-flight/
// ambiguous) -- it has no notion of "which prepaid" at all. The caller
// (PrepaidDetail.tsx) is responsible for keeping one
// PrepaidExpenseAttempt PER prepaid_id (a Map<number, PrepaidExpenseAttempt>)
// and always passing THIS prepaid's own slot into
// decidePrepaidExpenseAttempt / shouldRestoreAmbiguousAttempt -- never a
// single shared instance. This is deliberately NOT
// utils/fixedAssetPaymentIdempotency.ts's shape (which tracks one attempt
// for the whole component and blocks all other assets while it's
// unresolved) -- that cross-resource blocking behavior is the known,
// intentionally-unfixed Fixed Asset Payment legacy quirk and must not be
// copied: each Prepaid's slot starts idle and is usable independently, not
// that any one Prepaid's ambiguous state can pause every other Prepaid.
//
// Scoped to recognition ("Apply"/expense) ONLY: refund and write-off are
// different operations (neither has a frontend caller today -- see
// backend/prepaids_router.py's refund_prepaid/writeoff_prepaid, both
// server-side idempotent but with no UI) and must never share or block
// each other -- this module has no notion of refund or write-off at all.

export type PrepaidExpensePayload = {
  prepaid_id: number;
  amount: string;
  expense_account_id: number;
  date: string;
  memo: string | null;
};

/**
 * Deterministic canonical payload for POST /prepaids/{prepaid_id}/expense.
 * Callers must pass the SAME object (with idempotency_key added) as the
 * actual request body, never a separately reconstructed copy, so the
 * fingerprint can never drift from what is truly sent.
 *
 * - prepaid_id: canonicalized to a number, included even though it is also
 *   the path parameter -- the idempotency identity is only (company_id,
 *   operation_type, idempotency_key), so a key reused against a DIFFERENT
 *   prepaid must still be caught as a conflict by the backend. Also used
 *   locally by the caller (never sent as its own field beyond this
 *   payload) to select which per-prepaid attempt slot a submission reads
 *   and writes.
 * - amount: fixed 2-decimal string -- this app has no frontend-visible
 *   company money-scale setting anywhere (mirrors
 *   canonicalDepositWriteoff's identical rationale), matching the
 *   backend's own q2 (fixed CENT = Decimal("0.01")).
 * - expense_account_id: canonicalized to a number. Selects which expense
 *   account absorbs the recognized amount -- a same-key retry with a
 *   DIFFERENT expense account must never silently replay against the
 *   original account.
 * - date: the exact submitted YYYY-MM-DD -- never re-derived from "today"
 *   at retry time.
 * - memo: trimmed, empty string canonicalized to null -- matching the
 *   backend's own `(cmd.memo or "").strip() or None`.
 */
export function canonicalPrepaidExpense(
  prepaidId: string | number,
  amount: string | number,
  expenseAccountId: string | number,
  date: string,
  memo: string,
): PrepaidExpensePayload {
  return {
    prepaid_id: Number(prepaidId),
    amount: Number(amount).toFixed(2),
    expense_account_id: Number(expenseAccountId),
    date,
    memo: memo.trim() || null,
  };
}

export function fingerprintPrepaidExpense(payload: PrepaidExpensePayload): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle -- ONE slot's state machine. The caller owns a
// collection of these (one per prepaid_id), never a single shared instance.
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt for this prepaid. The next submission always
 *   mints a fresh key.
 * - "in-flight": a request has been sent for this prepaid and its outcome
 *   is not yet known. Transitions to "idle" (success or definite
 *   rejection) or "ambiguous" once the response (or its absence) is
 *   classified.
 * - "ambiguous": this prepaid's last attempt outcome could not be
 *   determined (network failure, timeout, unreadable response, HTTP 500,
 *   or any other unexpected server error). The key/fingerprint/payload are
 *   retained so an unchanged retry safely replays instead of risking a
 *   duplicate recognition; an edited retry stays blocked until this
 *   specific attempt is resolved. A DIFFERENT prepaid's own slot is
 *   entirely unaffected by this prepaid being ambiguous.
 */
export type PrepaidExpenseAttemptState = "idle" | "in-flight" | "ambiguous";

export type PrepaidExpenseAttempt = {
  state: PrepaidExpenseAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: PrepaidExpensePayload | null;
};

export const IDLE_PREPAID_EXPENSE_ATTEMPT: PrepaidExpenseAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type PrepaidExpenseSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: PrepaidExpenseAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state for THIS prepaid's own slot (the caller
 * is responsible for looking up the right slot -- e.g. from a
 * Map<number, PrepaidExpenseAttempt> keyed by prepaid_id -- before calling
 * this) and the canonical payload/fingerprint just built from the form.
 * Never mutates `attempt` -- returns a NEW attempt (state "in-flight") for
 * the caller to store back into that same slot when `kind === "start"`.
 *
 * This function has no concept of "a different prepaid" at all -- there is
 * nothing here analogous to fixedAssetPaymentIdempotency.ts's
 * blocked-cross-asset. Each prepaid's slot is independent by construction:
 * a prepaid with no retained attempt is always idle, regardless of what
 * any other prepaid's slot currently holds.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME fingerprint -> unchanged retry: reuses the stored key
 *    exactly (never mints a new one for an ambiguous outcome -- see module
 *    docstring).
 *  - ambiguous, DIFFERENT fingerprint -> blocked-mismatch: the prior
 *    attempt under this key may already have committed; an edited
 *    resubmission under the same key must never be sent.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decidePrepaidExpenseAttempt(
  attempt: PrepaidExpenseAttempt,
  payload: PrepaidExpensePayload,
  fingerprint: string,
  generateKey: () => string,
): PrepaidExpenseSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears this
 * prepaid's slot back to idle -- the caller should remove/overwrite ONLY
 * this prepaid's entry, never touch any other prepaid's slot. */
export function resolvePrepaidExpenseSuccess(): PrepaidExpenseAttempt {
  return IDLE_PREPAID_EXPENSE_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400/404 raised by this handler itself, 401/403
 * raised by its auth dependencies before the handler body ever runs, 422
 * from FastAPI's own request-validation short-circuit, a closed-period
 * 409, or an idempotency-conflict 409. In every one of these cases nothing
 * was persisted under this key, so the identity is free to reuse. Returns
 * to idle so the next submission mints a fresh key and is treated as a new
 * logical attempt, with the user free to edit any field. Only THIS
 * prepaid's slot is affected.
 */
export function resolvePrepaidExpenseDefiniteRejection(): PrepaidExpenseAttempt {
  return IDLE_PREPAID_EXPENSE_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before replay validation ran). Retains the key, fingerprint, and
 * submitted payload so an unchanged retry safely replays instead of
 * risking a duplicate; an edited retry stays blocked. Only THIS prepaid's
 * slot transitions -- every other prepaid's own slot is untouched and
 * remains independently usable (including immediately submitting its own
 * fresh attempt).
 */
export function resolvePrepaidExpenseAmbiguousFailure(
  attempt: PrepaidExpenseAttempt,
): PrepaidExpenseAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether the recognition form should repopulate this prepaid's previously
 * submitted form values (date/expense account/amount/memo) instead of
 * resetting to fresh defaults. True only when the slot passed in is a live
 * AMBIGUOUS attempt.
 *
 * Restoring matters: resetting to fresh defaults (e.g. today's date, the
 * currently-outstanding remaining amount) would almost never coincidentally
 * match what was actually submitted, so the very next submission would
 * compute a different fingerprint and get blocked as an "edited retry" --
 * trapping the user with no visible way to make an unchanged retry that
 * safely reuses the key.
 *
 * The caller must pass THIS prepaid's own slot (never a different
 * prepaid's) -- since this function has no prepaidId parameter at all,
 * correct scoping is entirely the caller's responsibility (look the slot
 * up from its Map by the CURRENT prepaid_id before calling this).
 */
export function shouldRestoreAmbiguousAttempt(attempt: PrepaidExpenseAttempt): boolean {
  return attempt.state === "ambiguous";
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type PrepaidExpenseFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: both a closed-period rejection and an idempotency-
 * conflict rejection share HTTP 409 (see
 * backend/utils/accounting_guards.py's _assert_not_closed, and
 * backend/prepaids_router.py's IdempotencyRequestConflict handling) but
 * require different messaging and a different interpretation -- a
 * closed-period 409 is detected via `isClosedPeriod`'s message match; any
 * OTHER 409 is treated as an idempotency conflict, mirroring
 * classifyDepositWriteoffFailure's identical reasoning.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce --
 * 400/404 raised directly by this handler, 401/403 raised by auth
 * dependencies (resolve before the handler body runs at all), and 422 from
 * FastAPI's own RequestValidationError handler (also short-circuits before
 * the handler runs) -- is treated as a definite rejection uniformly: every
 * one of these occurs strictly before (or in place of) any business
 * write, so nothing was ever persisted under the key.
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for
 * those -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response.
 */
export function classifyPrepaidExpenseFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): PrepaidExpenseFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    // Network failure, timeout, or any other rejection with no HTTP
    // response at all -- outcome unknown, must be treated as ambiguous.
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    // Every other 4xx (400, 401, 403, 404, 422, and any other well-formed
    // 4xx) -- see the module-level rationale above. Deterministic: nothing
    // was ever persisted under this key.
    return "definite-rejection";
  }

  // 5xx (500 and any other), and anything outside the 4xx/409 ranges this
  // app's own code never emits: ambiguous -- the backend's replay loader
  // (_load_prepaid_expense) runs AFTER db.commit(), so a 500 can mean the
  // recognition WAS persisted and only response construction or replay
  // validation failed. Fails closed for anything not positively identified
  // as a definite pre-commit rejection.
  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * Shown when an edited retry is blocked while an ambiguous attempt for
 * THIS prepaid remains unresolved. Deliberately does NOT say reloading
 * alone is safe -- a reload proves nothing about whether the prior attempt
 * already posted. Must convey all three ideas: (1) the prior attempt may
 * already have been recorded, (2) restoring the original values permits a
 * safe retry under the same key, (3) the user should check this prepaid's
 * history before starting a new recognition.
 */
export const PREPAID_EXPENSE_EDITED_RETRY_BLOCKED_MESSAGE =
  "This entry may already be recorded. Retry with the original details, or check the history below first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently
 * bound to a different (already-completed) request and can never succeed
 * for this payload. Deliberately does NOT suggest the recognition may
 * ambiguously have been recorded -- that is not the situation; THIS
 * attempt's data was never persisted under this key.
 */
export const PREPAID_EXPENSE_CONFLICT_MESSAGE =
  "Couldn't confirm this entry. Check the history below before trying again.";
